import { useQuery } from '@tanstack/react-query';

import { auth } from '../api/client';
import type { SessionUser } from '../api/types';
import { roleAllows, type AppRole } from './role';

export interface Session {
  /** The role of the signed-in user, or null while nobody is known yet. */
  role: AppRole | null;
  user: SessionUser | null;
  /** true while `/auth/me` has not answered (controls stay hidden, no flicker). */
  pending: boolean;
  canEdit: boolean;
  isAdmin: boolean;
}

/**
 * The session role, read straight out of the `['auth', 'me']` query cache that
 * `AppLayout`'s user chip populates on mount — one request per page load,
 * however many components ask (TASKS.md B7).
 *
 * With `AUTH_DISABLED=true` (dev, and the browser e2e suite) the api answers
 * `authDisabled` and the local user is an admin by construction, so every
 * control stays exactly where it was.
 */
export function useSession(): Session {
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => auth.me(signal),
    staleTime: 60_000,
    retry: false,
  });

  const authDisabled = Boolean(me.data?.authDisabled);
  const user = me.data?.user ?? null;
  const role = user?.role ?? null;

  return {
    role,
    user,
    pending: me.isPending,
    canEdit: roleAllows(role, 'edit') || authDisabled,
    isAdmin: roleAllows(role, 'admin') || authDisabled,
  };
}
